import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { getDocsKeyFromModelId } from "../../../Utils/ImportUtils";
import { createInstanceAndDocs } from "../../../../API/UploadToS3";
import { createInstance } from "../../../../API/UploadToS3";
import { getModel } from "../../../../API/ModelList";
import QRCode from "qrcode";
import { getDocByStatusWithInstance } from "../../../../API/DocByStatus";
import { createDocs } from "../../../../API/UploadToS3";

export function useDocumentKYCLogic(fileInputRef) {
  // Identifiants d'instance / document KYC
  const [kDocumentId, setKDocumentId] = useState(null);
  const { t } = useTranslation();
  const role = "User";

  // Numérotation des étapes et états généraux
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isIndividualPath, setIsIndividualPath] = useState(null);
  const [kInstanceId, setKInstanceId] = useState(null);

  // Données KYC (chemin personne physique)
  const [individualFirstName, setIndividualFirstName] = useState("");
  const [individualLastName, setIndividualLastName] = useState("");
  const [individualDOB, setIndividualDOB] = useState("");

  // Données KYC (chemin entreprise)
  const [companyName, setCompanyName] = useState("");
  const [companyRepresentativeFirstName, setCompanyRepresentativeFirstName] =
    useState("");
  const [companyRepresentativeName, setCompanyRepresentativeName] =
    useState("");
  const [companyRCS, setCompanyRCS] = useState("");
  const [hasProcuration, setHasProcuration] = useState(false);

  // Fichiers
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [droppedFileNames, setDroppedFileNames] = useState([]);
  const [addressFile, setAddressFile] = useState(null);
  const [addressFileName, setAddressFileName] = useState("");
  const [revenueFile, setRevenueFile] = useState(null);
  const [revenueFileName, setRevenueFileName] = useState("");
  const [kbisFile, setKbisFile] = useState(null);
  const [kbisFileName, setKbisFileName] = useState("");
  const [procurationFile, setProcurationFile] = useState(null);
  const [procurationFileName, setProcurationFileName] = useState("");
  const [pickerTarget, setPickerTarget] = useState(null);

  // Liste de modèles disponible
  const [modelList, setModelList] = useState([]);

  // État QR / lien sécurisé
  const [showQR, setShowQR] = useState(false);
  const [secureUrl, setSecureUrl] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");

  // Référence pour le polling
  const pollRef = useRef(null);

  //   Chargement des modèles  
  useEffect(() => {
    const idToken = Cookies.get("Token");
    getModel(idToken)
      .then((data) => {
        setModelList(data || []);
      })
      .catch((err) => {
        console.warn("Erreur récupération liste modèles :", err);
      });
  }, []);

  // Récupère types de fichiers supportés pour le modèle fixe
  const getSupportedFileTypes = useCallback(() => {
    const fixedModelId = "ah-dev-kyc01";
    const selectedModelData = modelList.find(
      (model) => model.ModelId === fixedModelId
    );
    return selectedModelData?.ModelSupportedFileTypes ?? [];
  }, [modelList]);

  // Mise à jour de l'état des fichiers (évite doublons)
  const updateFilesState = useCallback((newFiles) => {
    setSelectedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      const unique = newFiles.filter((f) => !existing.has(f.name));
      return [...prev, ...unique];
    });
    setDroppedFileNames((prev) => [...prev, ...newFiles.map((f) => f.name)]);
    setError(null);
  }, []);

  // Gestion drag & drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Validation des fichiers importés selon types supportés
  function filterSupportedFiles(files) {
    const supported = getSupportedFileTypes();
    return files.filter((f) =>
      supported.includes(f.name.split(".").pop().toLowerCase())
    );
  }

  // Handler pour drop
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer.files || []);
      const valid = filterSupportedFiles(files);
      if (valid.length !== files.length) {
        setError(t("Some files are not supported."));
      }
      if (pickerTarget && pickerTarget !== "final") {
        const f = valid[0];
        if (!f) {
          setPickerTarget(null);
          return;
        }
        switch (pickerTarget) {
          case "address":
            setAddressFile(f);
            setAddressFileName(f.name);
            break;
          case "revenue":
            setRevenueFile(f);
            setRevenueFileName(f.name);
            break;
          case "kbis":
            setKbisFile(f);
            setKbisFileName(f.name);
            break;
          case "procuration":
            setProcurationFile(f);
            setProcurationFileName(f.name);
            break;
          default:
            break;
        }
        setPickerTarget(null);
        setError(null);
        return;
      }
      updateFilesState(valid);
    },
    [pickerTarget, getSupportedFileTypes, updateFilesState, t]
  );

  // Handler pour input file
  const handleFileUpload = useCallback(
    (event) => {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      const valid = filterSupportedFiles(files);
      if (valid.length !== files.length) {
        setError(t("Some files are not supported."));
      }
      if (pickerTarget && pickerTarget !== "final") {
        const f = valid[0];
        if (!f) {
          event.target.value = null;
          return;
        }
        switch (pickerTarget) {
          case "address":
            setAddressFile(f);
            setAddressFileName(f.name);
            break;
          case "revenue":
            setRevenueFile(f);
            setRevenueFileName(f.name);
            break;
          case "kbis":
            setKbisFile(f);
            setKbisFileName(f.name);
            break;
          case "procuration":
            setProcurationFile(f);
            setProcurationFileName(f.name);
            break;
          default:
            break;
        }
        setPickerTarget(null);
        event.target.value = null;
        setError(null);
        return;
      }
      updateFilesState(valid);
      event.target.value = null;
    },
    [getSupportedFileTypes, pickerTarget, updateFilesState, t]
  );

  // Suppression d'un fichier spécifique
  const handleDeleteSpecificFile = useCallback((index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setDroppedFileNames((prev) => prev.filter((_, i) => i !== index));
    setError(null);
  }, []);

  // Suppression du dernier fichier
  const handleFileDelete = useCallback(() => {
    setSelectedFiles((prev) => prev.slice(0, -1));
    setDroppedFileNames((prev) => prev.slice(0, -1));
    setError(null);
  }, []);

  // Suppression d'un fichier d'étape
  const handleDeleteStepFile = useCallback((key) => {
    switch (key) {
      case "address":
        setAddressFile(null);
        setAddressFileName("");
        break;
      case "revenue":
        setRevenueFile(null);
        setRevenueFileName("");
        break;
      case "kbis":
        setKbisFile(null);
        setKbisFileName("");
        break;
      case "procuration":
        setProcurationFile(null);
        setProcurationFileName("");
        break;
      default:
        break;
    }
    setError(null);
  }, []);

  // Navigation précédente
  const handlePrevious = useCallback(() => {
    setError(null);
    if (step === 1) return;
    setStep((p) => p - 1);
  }, [step]);

  // Validation et navigation suivante
  const handleNext = useCallback(() => {
    setError(null);
    if (step === 1) {
      if (isIndividualPath === null) {
        setError(t("Please choose personal or company path."));
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (isIndividualPath === true) {
        if (!individualFirstName || !individualLastName || !individualDOB) {
          setError(t("Please fill in all fields."));
          return;
        }
        const dobDate = new Date(individualDOB);
        const today = new Date();
        dobDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        if (dobDate > today) {
          setError(t("Date of birth cannot be in the future."));
          return;
        }
      } else {
        if (
          !companyRepresentativeFirstName ||
          !companyRepresentativeName ||
          !companyName ||
          !companyRCS
        ) {
          setError(t("Please fill in all fields."));
          return;
        }
      }
      setStep(3);
      return;
    }
    if (step === 3) {
      if (isIndividualPath === true) {
        if (!addressFile) {
          setError(t("You need to add files to this step."));
          return;
        }
      } else {
        if (!kbisFile) {
          setError(t("You need to add files to this step."));
          return;
        }
      }
      setStep(4);
      return;
    }
    if (step === 4) {
      if (isIndividualPath === true) {
        if (!revenueFile) {
          setError(t("You need to add files to this step."));
          return;
        }
      } else {
        if (hasProcuration && !procurationFile) {
          setError(t("You need to add files to this step."));
          return;
        }
      }
      handleSubmitDocuments();
      return;
    }
  }, [
    step,
    isIndividualPath,
    individualFirstName,
    individualLastName,
    individualDOB,
    companyRepresentativeFirstName,
    companyRepresentativeName,
    companyName,
    companyRCS,
    hasProcuration,
    addressFile,
    revenueFile,
    kbisFile,
    procurationFile,
    selectedFiles,
    t,
  ]);

  // Définir le chemin et passer à l'étape suivante
  const setChoiceAndNext = useCallback((isIndividual) => {
    setIsIndividualPath(isIndividual);
    setError(null);
    setStep(2);
  }, []);

  // Ouvrir le picker pour une cible spécifique
  const openPickerFor = useCallback(
    (target) => {
      setPickerTarget(target);
      if (fileInputRef?.current) fileInputRef.current.click();
    },
    [fileInputRef]
  );

  //  Soumission des documents (création instance + docs) 
  const handleSubmitDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filesToUpload = [
        ...(addressFile ? [addressFile] : []),
        ...(revenueFile ? [revenueFile] : []),
        ...(kbisFile ? [kbisFile] : []),
        ...(procurationFile ? [procurationFile] : []),
        ...selectedFiles,
      ];

      if (filesToUpload.length === 0) {
        setError(t("You need to add files to your instance."));
        setLoading(false);
        return;
      }

      // Construire le jInput de base selon le type (individu / entreprise)
      const baseJInput = isIndividualPath
        ? {
            type: "individual",
            firstName: individualFirstName || "",
            lastName: individualLastName || "",
            dob: individualDOB || "",
          }
        : {
            type: "company",
            companyName: companyName || "",
            representativeFirstName: companyRepresentativeFirstName || "",
            representativeLastName: companyRepresentativeName || "",
            rcs: companyRCS || "",
          };

      // Construire les payloads JSON par fichier
      const builtFileJSONs = filesToUpload.map((f) => {
        const source =
          (addressFile && f === addressFile && "address") ||
          (revenueFile && f === revenueFile && "revenue") ||
          (kbisFile && f === kbisFile && "kbis") ||
          (procurationFile && f === procurationFile && "procuration") ||
          "final";
        return JSON.stringify({
          ...baseJInput,
          filename: f.name,
          source,
          createdAt: new Date().toISOString(),
        });
      });

      // Générer un nom d'instance automatique
      const now = new Date();
      const yyyymmdd = now.toISOString().split("T")[0].replace(/-/g, "");
      const instanceNameAuto = `${
        isIndividualPath ? "Part" : "Ent"
      }_${yyyymmdd}_${now.getHours()}${now.getMinutes()}${now.getSeconds()}`;

      const sFinalCustomerAuto = "KYC / KYB";
      const selectedModelFixed = "ah-dev-kyc01";
      const selectedClient = "kyckyb";
      const iExpectedDocsCalc = filesToUpload.length + 1;

      const idToken = Cookies.get("Token");

      // Créer l'instance (logique centrée utilisateur)
      let createdInstanceId = null;
      try {
        const createRes = await createInstance(
          instanceNameAuto,
          selectedModelFixed,
          iExpectedDocsCalc,
          idToken,
          sFinalCustomerAuto,
          selectedClient
        );
        createdInstanceId =
          createRes?.message?.InstanceId || createRes?.InstanceId || null;
      } catch (errCreate) {
        console.warn("Erreur critique lors de la création de l'instance:", errCreate);
        throw new Error("Erreur critique: Impossible de créer l'instance.");
      }
      if (!createdInstanceId) {
        throw new Error("Erreur: InstanceId non disponible après la création.");
      }

      // Création du document "identity.pdf" placeholder
      let createdDocId = null;
      try {
        const placeholderJson = JSON.stringify({
          filename: "identity.pdf",
          source: "identity",
          createdAt: new Date().toISOString(),
          note: "placeholder for Klippa / webhook",
        });

        const docRes = await createDocs(
          createdInstanceId,
          "identity.pdf",
          "application/pdf",
          idToken,
          placeholderJson,
          sFinalCustomerAuto
        );
        createdDocId =
          docRes?.message?.DocumentId ||
          docRes?.DocumentId ||
          docRes?.documentId ||
          null;

        if (createdDocId) {
          setKDocumentId(createdDocId);
          Cookies.set("kDocumentId", createdDocId);
        } else {
          console.warn("createDocs returned no DocumentId", docRes);
          throw new Error("Erreur: Impossible d'obtenir le DocumentId pour l'identité.");
        }
      } catch (eDoc) {
        console.warn("Erreur création document identity:", eDoc);
        throw new Error("Erreur critique: Impossible de créer le document identity. " + eDoc.message);
      }

      // Upload des autres documents (un par un) avec retry simple
      if (filesToUpload.length > 0) {
        await new Promise((r) => setTimeout(r, 500));
        for (let idx = 0; idx < filesToUpload.length; idx++) {
          const f = filesToUpload[idx];
          const fileJSONForThisFile = builtFileJSONs[idx] ? [builtFileJSONs[idx]] : [];
          let attempts = 0;
          while (attempts < 2) {
            attempts++;
            try {
              await createInstanceAndDocs(
                createdInstanceId,
                selectedModelFixed,
                [f],
                idToken,
                false,
                fileJSONForThisFile,
                null,
                sFinalCustomerAuto,
                sFinalCustomerAuto
              );
              break; // réussi -> passer au fichier suivant
            } catch (singleErr) {
              console.warn(`Upload attempt ${attempts} failed for ${f.name}:`, singleErr);
              if (attempts >= 2) {
                throw new Error(
                  `Erreur lors de la création du document pour le fichier ${f.name} : ${singleErr?.message || singleErr}`
                );
              }
              await new Promise((r) => setTimeout(r, 300));
            }
          }
        }
      }

      // Après succès : stocker identifiants et réinitialiser fichiers d'étape
      const finalInstanceId = createdInstanceId || instanceNameAuto;
      setKInstanceId(finalInstanceId);
      setKDocumentId(createdDocId);
      Cookies.set("kDocumentId", createdDocId);
      Cookies.set("modelId", selectedModelFixed);
      Cookies.set("instanceName", instanceNameAuto);
      Cookies.set("kInstanceId", finalInstanceId);

      setSelectedFiles([]);
      setDroppedFileNames([]);
      setAddressFile(null);
      setAddressFileName("");
      setRevenueFile(null);
      setRevenueFileName("");
      setKbisFile(null);
      setKbisFileName("");
      setProcurationFile(null);
      setProcurationFileName("");

      setLoading(false);
      setStep(5);
    } catch (e) {
      console.warn("Error submitting KYC documents (create instance):", e);
      setLoading(false);
      setError(t("Error in processing, please try again") + `: ${e.message || e}`);
      setStep(-1);
    }
  }, [
    kDocumentId,
    setKDocumentId,
    isIndividualPath,
    individualFirstName,
    individualLastName,
    individualDOB,
    companyName,
    companyRepresentativeFirstName,
    companyRepresentativeName,
    companyRCS,
    addressFile,
    revenueFile,
    kbisFile,
    procurationFile,
    selectedFiles,
    t,
  ]);

  // Finalisation après vérification côté localStorage (étape 5)
  const handleFinalizeLocal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      const result = localStorage.getItem("kyc_result");
      if (result) {
        const data = JSON.parse(result);
        if (data.status === "success") {
          setStep(6);
        } else {
          setError(t("Verification failed or was cancelled"));
          setStep(5);
        }
      } else {
        setError(t("No verification result found"));
        setStep(5);
      }
    } catch (err) {
      console.warn("Error in finalization:", err);
      setError(t("Error during finalization"));
      setStep(5);
    } finally {
      setLoading(false);
    }
  }, [t]);

  //  Polling statut documents 
  const clearStatusPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startStatusPoll = useCallback(() => {
    clearStatusPoll();
    const token = Cookies.get("Token");
    const cookieModelId = Cookies.get("modelId") || null;
    if (!token) {
      console.warn("startStatusPoll: no token, aborting poll");
      return;
    }
    if (!kInstanceId) {
      console.warn("startStatusPoll: no kInstanceId, aborting poll");
      return;
    }

    pollRef.current = setInterval(async () => {
      try {
        const apiReadyList = await getDocByStatusWithInstance(
          token,
          "ready",
          cookieModelId,
          kInstanceId
        );

        if (!Array.isArray(apiReadyList)) {
          console.warn(" unexpected readyList response:", apiReadyList);
          return;
        }

        const readyList = apiReadyList.filter(
          (doc) =>
            doc.InstanceId === kInstanceId ||
            doc.instanceId === kInstanceId ||
            doc?.message?.InstanceId === kInstanceId ||
            doc?.message?.instanceId === kInstanceId
        );

        if (readyList.length > 0) {
          console.log(` ${readyList.length} document(s) encore en 'ready'...`);
          return;
        }

        console.log(" aucun document en 'ready' → on passe à la suite");
        clearStatusPoll();
        setShowQR(false);
        setStep(6);
      } catch (err) {
        console.warn(" tick: Status poll error:", err);
      }
    }, 10000);
  }, [kInstanceId, clearStatusPoll]);

  // Lien sécurisé / QR  
  const generateSecureLink = useCallback(async () => {
    const instanceKeyLocal = kInstanceId || Cookies.get("kInstanceId") || Cookies.get("instanceName");
    const docKeyLocal = kDocumentId || Cookies.get("kDocumentId") || null;
    const payload = {
      instance: instanceKeyLocal || null,
      documentId: docKeyLocal || null,
      returnTo: window.location.pathname + window.location.search,
      isIndividualPath,
      individualFirstName: individualFirstName || "",
      individualLastName: individualLastName || "",
      individualDOB: individualDOB || "",
      companyName: companyName || "",
      companyRepresentativeFirstName: companyRepresentativeFirstName || "",
      companyRepresentativeName: companyRepresentativeName || "",
    };

    try {
      const encoded = btoa(encodeURIComponent(JSON.stringify(payload)));
      const path = "/User/DocumentKYC/SecureID";
      const idToken = Cookies.get("Token") || "";
      const urlWithDocAndInstance = `${path}?docid=${encodeURIComponent(docKeyLocal || "")}&kyc=${encodeURIComponent(encoded)}${idToken ? `&auth=${encodeURIComponent(idToken)}` : ""}`;
      const absoluteUrl = new URL(urlWithDocAndInstance, window.location.origin).toString();

      setSecureUrl(absoluteUrl);

      try {
        const dataUrl = await QRCode.toDataURL(absoluteUrl, { width: 260 });
        setQrDataUrl(dataUrl);
      } catch (err) {
        console.warn(" QR generation failed:", err);
        setQrDataUrl("");
      }

      setShowQR(true);
      startStatusPoll();
    } catch (err) {
      console.warn("failed to build payload/url:", err);
    }
  }, [
    kInstanceId,
    kDocumentId,
    isIndividualPath,
    individualFirstName,
    individualLastName,
    individualDOB,
    companyName,
    companyRepresentativeFirstName,
    companyRepresentativeName,
    startStatusPoll,
  ]);


  // Copier le lien dans le presse-papiers
  const copyLink = useCallback(() => {
    if (!secureUrl) {
      console.warn("no secureUrl to copy");
      return;
    }
    navigator.clipboard
      ?.writeText(secureUrl)
      .catch((err) => {
        console.warn("Clipboard write failed:", err);
      });
  }, [secureUrl]);

  // Fermer le QR et arrêter le poll
  const closeQR = useCallback(() => {
    clearStatusPoll();
    setShowQR(false);
  }, [clearStatusPoll]);

  // Nettoyage du poll au démontage
  useEffect(() => {
    return () => {
      clearStatusPoll();
    };
  }, [clearStatusPoll]);

  // Vérifier les paramètres URL kycComplete au montage (réponse mobile)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const kycComplete = urlParams.get("kycComplete");

    if (kycComplete) {
      console.log(`KYC terminé avec status=${kycComplete}, timer de fermeture 3s lancé`);
      if (kycComplete === "success") {
        setStep(6);
      } else if (kycComplete === "error") {
        setError(t("Identity verification failed"));
        setStep(5);
      }

      const timer = setTimeout(() => {
        console.log("Timer terminé, tentative de fermeture/redirection de la page");
        window.location.href = "about:blank";
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  // Exposer l'état et les handlers au composant
  const state = {
    step,
    loading,
    error,
    isIndividualPath,
    kDocumentId,
    role,
    individualFirstName,
    individualLastName,
    individualDOB,
    companyName,
    companyRepresentativeFirstName,
    companyRepresentativeName,
    companyRCS,
    hasProcuration,
    selectedFiles,
    droppedFileNames,
    addressFileName,
    revenueFileName,
    kbisFileName,
    procurationFileName,
    addressFile,
    revenueFile,
    kbisFile,
    procurationFile,
    pickerTarget,
    kInstanceId,
    showQR,
    secureUrl,
    qrDataUrl,
  };

  const handlers = {
    setStep,
    handleNext,
    handlePrevious,
    setChoiceAndNext,
    setIndividualFirstName,
    setIndividualLastName,
    setIndividualDOB,
    setCompanyName,
    setCompanyRepresentativeFirstName,
    setCompanyRepresentativeName,
    setCompanyRCS,
    setHasProcuration,
    handleFileUpload,
    handleDrop,
    handleDragOver,
    handleDeleteSpecificFile,
    handleFileDelete,
    openPickerFor,
    setPickerTarget,
    handleDeleteStepFile,
    handleSubmitDocuments,
    handleFinalizeLocal,
    generateSecureLink,
    copyLink,
    closeQR,
  };

  const secureModalPath = "/User/DocumentKYC/SecureID";

  const computed = {
    t,
    getSupportedFileTypes,
    finalRoute: `/User/${getDocsKeyFromModelId("ah-dev-kyc01")}`,
    secureModalPath,
  };

  return { state, handlers, computed };
}
